import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import {
  KeyRound,
  FileText,
  UserCheck,
  CreditCard,
  FolderLock,
  BellRing,
  CheckSquare,
  StickyNote,
  Terminal,
  Wifi,
  Bookmark,
  ShieldAlert,
  AppWindow,
  Wallet,
} from 'lucide-react';
import { getApplications, getPinnedApplications, setPinnedApplications } from '../api/applications';
import { useAuth } from '../contexts/AuthContext';

export const APP_ICON_MAP = {
  KeyRound,
  FileText,
  UserCheck,
  CreditCard,
  FolderLock,
  BellRing,
  CheckSquare,
  StickyNote,
  Terminal,
  Wifi,
  Bookmark,
  ShieldAlert,
  AppWindow,
  Wallet,
  passwords: KeyRound,
  notes: FileText,
  identities: UserCheck,
  cards: CreditCard,
  documents: FolderLock,
  reminders: BellRing,
  tasks: CheckSquare,
  notepad: StickyNote,
  'ssh-keys': Terminal,
  wifi: Wifi,
  bookmarks: Bookmark,
  'recovery-codes': ShieldAlert,
  'expense-tracker': Wallet,
  expenses: Wallet,
};

export const APP_COLOR_MAP = {
  passwords: '#3b82f6',
  notes: '#10b981',
  identities: '#8b5cf6',
  cards: '#f59e0b',
  documents: '#06b6d4',
  reminders: '#ec4899',
  tasks: '#14b8a6',
  notepad: '#eab308',
  'ssh-keys': '#6366f1',
  wifi: '#0ea5e9',
  bookmarks: '#f97316',
  'recovery-codes': '#ef4444',
  'expense-tracker': '#059669',
  expenses: '#059669',
};

const ApplicationsContext = createContext(null);

export function ApplicationsProvider({ children }) {
  const { user } = useAuth();
  const [applications, setApplications] = useState(() => {
    try {
      const c = sessionStorage.getItem('pm_cached_applications');
      return c ? JSON.parse(c) : [];
    } catch {
      return [];
    }
  });
  const [pinnedApplications, setPinnedApplicationsState] = useState(() => {
    try {
      const c = sessionStorage.getItem('pm_cached_pinned_apps');
      return c ? JSON.parse(c) : [];
    } catch {
      return [];
    }
  });
  const [loading, setLoading] = useState(() => {
    try {
      return !sessionStorage.getItem('pm_cached_applications');
    } catch {
      return true;
    }
  });

  const fetchApps = useCallback(async () => {
    if (!user) {
      setApplications([]);
      setPinnedApplicationsState([]);
      setLoading(false);
      try {
        sessionStorage.removeItem('pm_cached_applications');
        sessionStorage.removeItem('pm_cached_pinned_apps');
      } catch {}
      return;
    }
    try {
      const [allApps, pinned] = await Promise.all([
        getApplications(),
        getPinnedApplications(),
      ]);
      if (allApps) {
        setApplications(allApps);
        try { sessionStorage.setItem('pm_cached_applications', JSON.stringify(allApps)); } catch {}
      }
      if (pinned) {
        setPinnedApplicationsState(pinned);
        try { sessionStorage.setItem('pm_cached_pinned_apps', JSON.stringify(pinned)); } catch {}
      }
    } catch (err) {
      console.error('Failed to load applications:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchApps();
  }, [fetchApps]);

  const updatePinned = async (appIdsOrSlugs) => {
    try {
      const updated = await setPinnedApplications(appIdsOrSlugs);
      setPinnedApplicationsState(updated || []);
      return updated;
    } catch (err) {
      console.error('Failed to update pinned applications:', err);
      throw err;
    }
  };

  const hasPermission = useCallback((slug, level = 'access') => {
    if (!user) return false;
    if (['SUPER_ADMIN', 'ADMIN'].includes(user.role)) return true;

    const app = applications.find(a => a.slug === slug);
    if (!app || !app.enabled) return false;
    if (!app.permissions) return true; // Default fallback
    return Boolean(app.permissions[level]);
  }, [user, applications]);

  return (
    <ApplicationsContext.Provider
      value={{
        applications,
        pinnedApplications,
        loading,
        refreshApplications: fetchApps,
        updatePinned,
        hasPermission,
      }}
    >
      {children}
    </ApplicationsContext.Provider>
  );
}

export function useApplications() {
  const context = useContext(ApplicationsContext);
  if (!context) {
    throw new Error('useApplications must be used within an ApplicationsProvider');
  }
  return context;
}
